import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useToast } from "@/hooks/use-toast";
import { X, UserPlus } from "lucide-react";
import { ContactRecord } from "@/hooks/useContacts";
import { ParticipantDraft, buildParticipantKey, normaliseEmail } from "@/types/participants";
import { validateEmail } from "@/utils/security";

interface ParticipantEditorProps {
  participants: ParticipantDraft[];
  onChange: (participants: ParticipantDraft[]) => void;
  availableContacts: ContactRecord[];
  onCreatePendingContact?: (contact: ContactRecord) => void;
  allowManualEntry?: boolean;
  emptyHint?: string;
}

export const ParticipantEditor = ({
  participants,
  onChange,
  availableContacts,
  onCreatePendingContact,
  allowManualEntry = true,
  emptyHint = "No participants added yet.",
}: ParticipantEditorProps) => {
  const { toast } = useToast();
  const [searchOpen, setSearchOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const nameInputId = useMemo(() => `participant-name-${Math.random().toString(36).slice(2)}`, []);
  const emailInputId = useMemo(() => `participant-email-${Math.random().toString(36).slice(2)}`, []);

  const selectedEmails = useMemo(() => {
    return new Set(participants.map((participant) => normaliseEmail(participant.email)));
  }, [participants]);

  const contactsToSuggest = useMemo(() => {
    return availableContacts.filter((contact) => !selectedEmails.has(normaliseEmail(contact.email)));
  }, [availableContacts, selectedEmails]);

  const handleRemove = (key: string) => {
    onChange(participants.filter((participant) => participant.key !== key));
  };

  const handleSelectContact = (contact: ContactRecord) => {
    const email = normaliseEmail(contact.email);
    if (selectedEmails.has(email)) {
      toast({
        title: "Already added",
        description: `${contact.email} is already a participant for this form.`,
      });
      return;
    }

    const draft: ParticipantDraft = {
      key: buildParticipantKey(email, contact.id),
      contactId: contact.id,
      email,
      name: contact.name ?? null,
      isNewContact: false,
    };

    onChange([...participants, draft]);
    setSearchOpen(false);
  };

  const handleManualAdd = () => {
    const email = normaliseEmail(newEmail);
    if (!validateEmail(email)) {
      toast({
        title: "Invalid email",
        description: "Please provide a valid email address before adding.",
        variant: "destructive",
      });
      return;
    }

    if (selectedEmails.has(email)) {
      toast({
        title: "Already added",
        description: `${email} is already a participant for this form.`,
      });
      return;
    }

    const contact: ContactRecord = {
      id: `temp-${buildParticipantKey(email)}`,
      name: newName.trim() ? newName.trim() : null,
      email,
    };

    const draft: ParticipantDraft = {
      key: buildParticipantKey(email, Math.random().toString(36).slice(2)),
      contactId: undefined,
      email,
      name: contact.name,
      isNewContact: true,
    };

    onChange([...participants, draft]);
    onCreatePendingContact?.(contact);

    setNewEmail("");
    setNewName("");
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Participants</Label>
        {participants.length === 0 ? (
          <p className="text-sm text-muted-foreground">{emptyHint}</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {participants.map((participant) => (
              <Badge key={participant.key} variant="secondary" className="flex items-center gap-2">
                <span className="max-w-[200px] truncate">
                  {participant.name ? `${participant.name} · ${participant.email}` : participant.email}
                </span>
                <button
                  type="button"
                  className="rounded-full border border-transparent hover:border-border hover:bg-background p-0.5"
                  onClick={() => handleRemove(participant.key)}
                  aria-label={`Remove ${participant.email}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Popover open={searchOpen} onOpenChange={setSearchOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" className="flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              Add from contacts
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0">
            <Command>
              <CommandInput placeholder="Search contacts" />
              <CommandList>
                <CommandEmpty>No contacts found.</CommandEmpty>
                <CommandGroup>
                  {contactsToSuggest.map((contact) => (
                    <CommandItem
                      key={contact.id}
                      value={contact.email}
                      onSelect={() => handleSelectContact(contact)}
                      className="data-[selected=true]:bg-primary data-[selected=true]:text-primary-foreground"
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-medium data-[selected=true]:text-primary-foreground">
                          {contact.name ?? contact.email}
                        </span>
                        {contact.name && (
                          <span className="text-xs text-muted-foreground data-[selected=true]:text-primary-foreground/80">
                            {contact.email}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {allowManualEntry && (
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor={nameInputId} className="text-xs text-muted-foreground">
                Name
              </Label>
              <Input
                id={nameInputId}
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                placeholder="Optional"
                className="w-44"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor={emailInputId} className="text-xs text-muted-foreground">
                Email
              </Label>
              <Input
                id={emailInputId}
                value={newEmail}
                onChange={(event) => setNewEmail(event.target.value)}
                placeholder="participant@example.com"
                className="w-64"
              />
            </div>
            <Button type="button" onClick={handleManualAdd} className="mt-2 sm:mt-0">
              Add participant
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
